/**
 * Before the visit (2026-11-03; T0, dark): one building's residents to look at, docked beside the table.
 * Read-only: it shows facts and writes nothing. A resident carrying two reasons is under both.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { MfLoadFailure, MfSkeleton } from './mfStates';
import { refusalWords } from './resubmittalsApi';
import { ResidentName } from './board/residents';
import { ToneChip, type Tone } from './board/chips';
import { previsitApi, type PrevisitBuildingDetail, type PrevisitResident, type PrevisitWhy } from './previsitApi';

export const WHY_ORDER: PrevisitWhy[] = ['NEW_REFERRAL', 'ANNUAL_DUE', 'MCD_ISSUE'];
export const WHY_LABEL: Record<PrevisitWhy, string> = {
  NEW_REFERRAL: 'New referral',
  ANNUAL_DUE: 'Annual review due (CE TWL date)',
  MCD_ISSUE: 'Medicaid issue (no audiogram in 12 months)',
};
export const WHY_MEANS: Record<PrevisitWhy, string> = {
  NEW_REFERRAL: 'A Prospect, tagged NEW REFERRAL in CounselEAR, or created in eMerge in the last 60 days. Listed when there is no audiogram in the last 365 days.',
  ANNUAL_DUE: 'The TWL date in CounselEAR plus 12 months falls on or before the visit day. Always listed. Until a CounselEAR TWL date is on file, the annual date from Monday stands in, and the row says so.',
  MCD_ISSUE: 'Medicaid pending, none, the wrong type, or lost. Listed when there is no audiogram in the last 365 days.',
};
const WHY_TONE: Record<PrevisitWhy, Tone> = { NEW_REFERRAL: 'ok', ANNUAL_DUE: 'quiet', MCD_ISSUE: 'attn' };

export const WhyChip = ({ why }: { why: PrevisitWhy }) => <ToneChip tone={WHY_TONE[why]} title={WHY_MEANS[why]}>{WHY_LABEL[why]}</ToneChip>;

const words = (s: string | null) => (s ? s.toLowerCase().replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase()) : null);
const dollars = (cents: number) => `$${(cents / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
/** YYYY-MM-DD as MM/DD/YYYY, read as parts so no timezone can move the day. */
export const mdy = (d: string | null) => (d && /^\d{4}-\d{2}-\d{2}/.test(d) ? `${d.slice(5, 7)}/${d.slice(8, 10)}/${d.slice(0, 4)}` : '—');

function Facts({ r }: { r: PrevisitResident }) {
  return (
    <dl className="mf-facts">
      <dt>Testing status</dt><dd>{words(r.testing_status) || <span className="board-na">none recorded</span>}</dd>
      <dt>Patient liability</dt>
      <dd>{r.liability_cents === null ? <span className="board-na">none recorded</span> : `${dollars(r.liability_cents)}${r.liability_status ? ` (${words(r.liability_status)})` : ''}`}</dd>
      <dt>Annual date</dt><dd>{mdy(r.annual_date)} <span className="board-src">{r.annual_date_label}</span></dd>
      <dt>Medicaid number</dt><dd>{r.has_medicaid_number ? 'On file' : <span className="board-na">not on file</span>}</dd>
      <dt>SSN</dt><dd>{r.has_ssn ? 'On file' : <span className="board-na">not on file</span>}</dd>
    </dl>
  );
}

export default function PrevisitPanel({ facilityId }: { facilityId: string }) {
  const [data, setData] = useState<PrevisitBuildingDetail | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const load = useCallback(async () => {
    setProblem(null);
    try {
      const r = await previsitApi.building(facilityId);
      if (r.success && r.data) setData(r.data);
      else setProblem(refusalWords(r, 'Could not load this building.'));
    } catch { setProblem('Could not reach eMerge.'); }
  }, [facilityId]);
  useEffect(() => { setData(null); load(); }, [load]);

  if (problem) return <MfLoadFailure message={problem} retry={load} />;
  if (!data) return <MfSkeleton bare />;
  const x = data.excluded;
  return (
    <div className="mf-panel">
      <p>
        Not listed: {x.recent_audiogram} with an audiogram in the last year, {x.enrolled} enrolled, {x.other} with no reason to look. Visit {mdy(data.next_visit)}
        {data.provider_name ? ` with ${data.provider_name}` : ''}.
      </p>
      {WHY_ORDER.map((why) => {
        const group = data.residents.filter((r) => r.why.includes(why));
        return (
          <section key={why} aria-label={WHY_LABEL[why]}>
            <h3><WhyChip why={why} /> <span className="board-src">{group.length}</span></h3>
            {group.length === 0 ? <p className="board-na">No one.</p> : (
              <ul>
                {group.map((r) => (
                  <li key={r.id}>
                    <ResidentName p={r} /> {r.audiogram_in_12_months ? <span className="board-src">Audiogram in the last year</span> : null}
                    <Facts r={r} />
                  </li>
                ))}
              </ul>
            )}
          </section>
        );
      })}
    </div>
  );
}
